import Dialog from "@mui/material/Dialog"
import Button from "@mui/material/Button"
import { useContext, useEffect, useRef, useState } from "react"
import { ArcElement, Chart as ChartJS, Legend, Tooltip, type ChartType, type DefaultDataPoint } from "chart.js"
import { Pie } from "react-chartjs-2"
import ExportChartDialog from "../../ExportChartDialog"
import ExportChartViewModel from "../../ViewModels/ExportChartViewModel"
import { APIContext } from "../../../Contexts/APIContext"
import { DialogActions } from "@mui/material"
import Grid from "@mui/material/Grid2"
import PieChartOptions from "../../../Utilities/PieChartOptions"
import PieChartOptionsViewModel from "../../../TripRate/ViewModels/PieChartOptionsViewModel"
import MinMaxParam from "../../../TripRate/ViewModels/MinMaxViewModel"
import { getPieBackgroundColours, getPieBorderColours } from "../../../Utilities/PieChartPalette"

ChartJS.register(ArcElement, Tooltip, Legend)

interface PieSegment {
    label: string
    percentage: number
}

interface IProps {
    onClose: () => void
    open: boolean
    surveyId: string
    date: string
}

type ChartOrUndef<TType extends ChartType = ChartType, TData = DefaultDataPoint<TType>, TLabel = unknown> =
    ChartJS<TType, TData, TLabel> | undefined

export default function OGVSplitChart({ open, onClose, surveyId, date }: IProps) {
    const { post, postPDF, get } = useContext(APIContext)
    const [data, setData] = useState<PieSegment[]>([])
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const [minMaxTimes, setMinMaxTimes] = useState<MinMaxParam>(new MinMaxParam())
    const [pieOptions, setPieOptions] = useState<PieChartOptionsViewModel>(new PieChartOptionsViewModel())
    const pieOptionsRef = useRef<PieChartOptionsViewModel>(pieOptions)
    const chartRef = useRef<ChartOrUndef<"pie", number[], string>>(undefined)

    useEffect(() => {
        if (open) {
            getSurveyTimes()
            getData()
        }
    }, [open])

    const getData = (options?: PieChartOptionsViewModel) => {
        const vm = { surveyId, date, options }
        post("Chart", "GetOGVChartForSurvey", vm, setData)
    }

    const getSurveyTimes = () => {
        get(`SurveyDays`, `GetAvailableTimeRangeForSurvey?surveyReference=${surveyId}`, setMinMaxTimes)
    }

    const exportGraph = () => setExportDialogOpen(true)

    const getReport = (exportFilters: ExportChartViewModel) => {
        const exportOptions = pieOptionsRef.current
        exportFilters.data = data
        exportFilters.surveyDate = date
        exportFilters.surveyRef = surveyId
        exportFilters.options = exportOptions
        exportFilters.chartTitle = "OGV Split Percentages"
        exportFilters.type === "PDF" ? getChartPDF(exportFilters) : getChartJPEG(exportOptions)
    }

    const getChartPDF = (exportFilters: ExportChartViewModel) => {
        postPDF("PDFGenerator", "GetModalSplitPiePDF", exportFilters.fileName, false, exportFilters)
    }

    const getChartJPEG = (exportOptions: PieChartOptionsViewModel) => {
        downloadJPEG(exportOptions)
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        customCanvasBackgroundColor: { color: "#ffffff" },
        backgroundColor: "#ffffff",
        plugins: {
            backgroundColor: "#ffffff",
            legend: { display: true },
            title: {
                display: true,
                text: `OGV Split Percentages For: ${surveyId} Surveyed: ${date}`,
                font: { size: 14, weight: "bold" as const },
                padding: 20,
            },
            tooltip: {
                displayColors: false,
                callbacks: {
                    title: () => "",
                    label: (context: any) => {
                        const item = data[context.dataIndex]
                        return [`${item.label}`, `Percentage: ${item.percentage.toFixed(1) || 0}%`]
                    },
                },
            },
        },
    }

    const chartData = {
        labels: data.map((m) => `${m.label} ${m.percentage.toFixed(1)}%`),
        datasets: [
            {
                data: data.map((d) => d.percentage),
                dataLabels: data.map((d) => d.percentage.toFixed(1) + "%"),
                backgroundColor: getPieBackgroundColours(data),
                borderColor: getPieBorderColours(data),
                borderWidth: 1,
            },
        ],
    }
    const getPeriodLabel = (id: string) => {
        switch (id) {
            case ("all"):
                return "All Times";
            case ("select"):
                return "User Selected Period";
            case ("peak"):
                return "Peak Period"
            default:
                return "";
        }

    }


    const downloadJPEG = (exportOptions: PieChartOptionsViewModel) => {
        const chart = chartRef.current
        if (!chart) return

        const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

        const src = chart.canvas as HTMLCanvasElement
        const padding = 48
        const out = document.createElement("canvas")
        out.width = src.width
        out.height = src.height + padding
        const octx = out.getContext("2d")
        if (!octx) return

        octx.fillStyle = "#ffffff"
        octx.fillRect(0, 0, out.width, out.height)
        octx.drawImage(src, 0, 0)

        let infoText = `Direction: ${capitalize(exportOptions.direction)}, Period: ${getPeriodLabel(exportOptions.period)}`
        if (exportOptions.selectedTime) {
            infoText += `, Time: ${exportOptions.selectedTime.min}:00 - ${exportOptions.selectedTime.max}:00`
        }

        octx.fillStyle = "#000000"
        octx.font = "16px Arial"
        octx.textAlign = "center"
        octx.fillText(infoText, out.width / 2, src.height + 28)

        const dataUrl = out.toDataURL("image/jpeg", 0.95)
        const a = document.createElement("a")
        a.href = dataUrl
        a.download = `ogv-split-${surveyId}-${date}.jpg`
        a.click()
    }

    const handleOptionsChange = (options: PieChartOptionsViewModel) => {
        pieOptionsRef.current = options
        setPieOptions(options)
        getData(options)
    }

    return (
        <Dialog open={open} fullWidth maxWidth="md" onClose={onClose}>
            <div style={{ padding: 20 }}>
                <div style={{ width: 500, height: 500, margin: "0 auto" }}>
                    <Pie ref={chartRef} data={chartData} options={options} />
                </div>
                <Grid container style={{ marginTop: 20 }}>
                    <Grid size={12}>
                        <PieChartOptions
                            minMaxTimes={minMaxTimes}
                            getOptions={handleOptionsChange}
                            onOptionsChange={handleOptionsChange}
                        />
                    </Grid>
                </Grid>
            </div>
            <DialogActions>
                <Button fullWidth={false} onClick={() => onClose()}>Close</Button>
                <Button fullWidth={false} onClick={exportGraph}>Export</Button>
            </DialogActions>
            <ExportChartDialog
                dialogOpen={exportDialogOpen}
                handleClose={() => setExportDialogOpen(false)}
                exportData={getReport}
            />
        </Dialog>
    )
}
